import type { Color, IconId } from '@ic/engine';

// Original piece artwork, drawn on a 100×100 grid. Every piece shares the same
// pedestal so the custom pieces (Dragon, Bomb, Miner) sit in the same family.

export type PieceSet = 'classic' | 'neon';

interface Palette { fill: string; stroke: string; detail: string; accent: string; metal: string; leaf: string }

/** Piece colours: the two chess sides plus Green, Yellow and grey (out) for four players. */
export type PieceColor = Color | 'g' | 'y' | 'x';

const PALETTES: Record<PieceSet, Record<PieceColor, Palette>> = {
  classic: {
    w: { fill: '#fbf7ee', stroke: '#2b2722', detail: '#2b2722', accent: '#d97757', metal: '#c2bcb2', leaf: '#7f9f5c' },
    b: { fill: '#3b3631', stroke: '#1c1916', detail: '#efe7d8', accent: '#e8875f', metal: '#8f877c', leaf: '#93b26f' },
    g: { fill: '#6f9f5a', stroke: '#22351b', detail: '#f3f7ea', accent: '#e8c55f', metal: '#b9cdb0', leaf: '#d8eec4' },
    y: { fill: '#f0c64a', stroke: '#4a3410', detail: '#4a3410', accent: '#d97757', metal: '#d8c08a', leaf: '#7f9f5c' },
    x: { fill: '#a9a59d', stroke: '#5f5b55', detail: '#5f5b55', accent: '#8c877f', metal: '#bdb8af', leaf: '#8c877f' },
  },
  neon: {
    w: { fill: 'rgba(40,240,255,0.14)', stroke: '#3ef2ff', detail: '#3ef2ff', accent: '#fff36b', metal: '#3ef2ff', leaf: '#7dffb0' },
    b: { fill: 'rgba(255,70,210,0.16)', stroke: '#ff4fd8', detail: '#ff4fd8', accent: '#fff36b', metal: '#ff4fd8', leaf: '#7dffb0' },
    g: { fill: 'rgba(90,255,120,0.16)', stroke: '#5cff7a', detail: '#5cff7a', accent: '#fff36b', metal: '#5cff7a', leaf: '#7dffb0' },
    y: { fill: 'rgba(255,220,60,0.16)', stroke: '#ffd93b', detail: '#ffd93b', accent: '#ff7ad9', metal: '#ffd93b', leaf: '#7dffb0' },
    x: { fill: 'rgba(160,160,170,0.12)', stroke: '#8d8d99', detail: '#8d8d99', accent: '#8d8d99', metal: '#8d8d99', leaf: '#8d8d99' },
  },
};

const BASE = 'M24 91H76a3 3 0 0 0 3-3v-3a6 6 0 0 0-6-6H27a6 6 0 0 0-6 6v3a3 3 0 0 0 3 3Z';
const COLLAR = 'M31 79H69L66 72H34Z';

function Body({ icon, p }: { icon: IconId; p: Palette }) {
  const main = { fill: p.fill, stroke: p.stroke };
  const line = { fill: 'none', stroke: p.detail, strokeWidth: 2.5 };
  switch (icon) {
    case 'pawn':
      return (<>
        <path {...main} d="M38 72Q40 58 44 54H56Q60 58 62 72Z" />
        <rect {...main} x="39" y="49" width="22" height="6" rx="3" />
        <circle {...main} cx="50" cy="38" r="12" />
      </>);
    case 'rook':
      return (<>
        <path {...main} d="M33 72L36 44H64L67 72Z" />
        <path {...main} d="M30 44V22H38V29H46V22H54V29H62V22H70V44Z" />
        <path {...line} d="M37 51H63M35 64H65" />
      </>);
    case 'bishop':
      return (<>
        <path {...main} d="M36 72Q38 62 44 58H56Q62 62 64 72Z" />
        <path {...main} d="M50 18C64 30 66 44 58 56H42C34 44 36 30 50 18Z" />
        <path {...line} d="M54 30L46 40" />
        <circle {...main} cx="50" cy="14" r="4.5" />
      </>);
    case 'knight':
      return (<>
        <path {...main} d="M32 72C30 60 36 54 44 48C38 50 30 54 25 50C20 46 24 38 32 34L44 22L45 11L52 20C68 22 76 40 70 72Z" />
        <circle cx="41" cy="32" r="2.8" fill={p.detail} />
        <path {...line} d="M55 24C63 31 66 45 64 62" />
      </>);
    case 'queen':
      return (<>
        <path {...main} d="M30 72L22 34L36 52L42 28L50 50L58 28L64 52L78 34L70 72Z" />
        {[[22, 30], [42, 24], [58, 24], [78, 30]].map(([x, y]) => <circle key={x} {...main} cx={x} cy={y} r="4.5" />)}
        <path {...line} d="M32 64H68" />
      </>);
    case 'king':
      return (<>
        <path {...main} d="M30 72Q26 50 38 44H62Q74 50 70 72Z" />
        <path {...main} d="M46 10H54V18H62V26H54V40H46V26H38V18H46Z" />
        <path {...line} d="M34 62H66M38 44Q50 38 62 44" />
      </>);
    case 'dragon':
      return (<>
        {/* rook-style crenellated base */}
        <path {...main} d="M33 74V61H39V66H46V61H54V66H61V61H67V74Z" />
        {/* wings */}
        <path {...main} d="M42 52C30 46 20 34 9 20C13 34 12 43 7 52C17 50 23 54 26 61C30 57 36 57 41 59Z" />
        <path {...main} d="M58 52C70 46 80 34 91 20C87 34 88 43 93 52C83 50 77 54 74 61C70 57 64 57 59 59Z" />
        <path {...line} d="M11 24L30 52M89 24L70 52" />
        {/* torso and horned head */}
        <path {...main} d="M40 61Q37 48 44 40H56Q63 48 60 61Z" />
        <path {...main} d="M42 41L40 27L33 15L44 22L50 17L56 22L67 15L60 27L58 41Z" />
        <circle cx="45" cy="30" r="2.6" fill={p.accent} />
        <circle cx="55" cy="30" r="2.6" fill={p.accent} />
        <path {...line} d="M45 37Q50 40 55 37" />
      </>);
    case 'bomb':
      return (<>
        <path {...main} d="M39 72Q42 68 46 67H54Q58 68 61 72Z" />
        <circle {...main} cx="50" cy="49" r="21" />
        <rect {...main} x="44" y="23" width="12" height="8" rx="2" />
        <path fill="none" stroke={p.detail} strokeWidth="3" strokeLinecap="round" d="M50 23C50 15 57 11 64 13" />
        <path {...line} strokeLinecap="round" d="M37 44Q39 37 46 34" />
        <g className="spark">
          <path fill={p.accent} d="M66 4L68.5 10L75 12.5L68.5 15L66 21L63.5 15L57 12.5L63.5 10Z" />
          <circle cx="66" cy="12.5" r="2.4" fill="#fff" />
        </g>
      </>);
    case 'miner':
      return (<>
        {/* pickaxe held at the side */}
        <path fill="none" stroke={p.detail} strokeWidth="4" strokeLinecap="round" d="M30 72L22 30" />
        <path fill={p.metal} stroke={p.stroke} strokeWidth="2.5" d="M9 33Q20 21 36 27L35 31Q21 27 12 36Z" />
        {/* bishop-style body */}
        <path {...main} d="M36 72Q38 62 44 58H56Q62 62 64 72Z" />
        <path {...main} d="M40 59Q38 50 44 48H56Q62 50 60 59Z" />
        <circle {...main} cx="50" cy="39" r="9.5" />
        {/* helmet and lamp */}
        <path className="lamp-beam" fill={p.accent} opacity="0.35" d="M54 28L84 17L86 33Z" />
        <path {...main} d="M38 37Q38 21 50 21Q62 21 62 37Z" />
        <path fill="none" stroke={p.stroke} strokeWidth="3" strokeLinecap="round" d="M35 37H65" />
        <circle cx="50" cy="28.5" r="4.2" fill={p.accent} stroke={p.stroke} strokeWidth="2" />
      </>);
    case 'frog':
      return (<>
        {/* folded back legs */}
        <path {...main} d="M23 73Q15 62 26 56L35 66Z" />
        <path {...main} d="M77 73Q85 62 74 56L65 66Z" />
        {/* squat body and wide head */}
        <path {...main} d="M29 73Q25 51 50 46Q75 51 71 73Z" />
        <path {...main} d="M28 50Q27 33 50 33Q73 33 72 50Q50 59 28 50Z" />
        {/* big eyes on top */}
        <circle {...main} cx="38" cy="30" r="9" />
        <circle {...main} cx="62" cy="30" r="9" />
        <circle cx="39" cy="29" r="3.4" fill={p.detail} />
        <circle cx="63" cy="29" r="3.4" fill={p.detail} />
        <path {...line} strokeLinecap="round" d="M37 46Q50 52 63 46" />
        <path {...line} strokeLinecap="round" d="M42 64Q50 60 58 64" />
      </>);
    case 'gardener':
      return (<>
        {/* seedling */}
        <path fill="none" stroke={p.leaf} strokeWidth="3" strokeLinecap="round" d="M20 72V56" />
        <path fill={p.leaf} stroke={p.stroke} strokeWidth="2" d="M20 62Q11 59 12 51Q20 53 20 62Z" />
        <path fill={p.leaf} stroke={p.stroke} strokeWidth="2" d="M20 58Q28 54 27 46Q19 49 20 58Z" />
        {/* body */}
        <path {...main} d="M36 72Q38 62 44 58H56Q62 62 64 72Z" />
        <path {...main} d="M40 59Q38 50 44 48H56Q62 50 60 59Z" />
        <circle {...main} cx="50" cy="39" r="9.5" />
        {/* straw hat */}
        <path {...main} d="M28 34Q50 25 72 34Q50 41 28 34Z" />
        <path {...main} d="M41 32Q41 19 50 19Q59 19 59 32Z" />
        <path fill="none" stroke={p.accent} strokeWidth="3" d="M41.5 29.5H58.5" />
        {/* watering can */}
        <path fill={p.metal} stroke={p.stroke} strokeWidth="2.5" d="M63 53H78V67H63Z" />
        <path fill="none" stroke={p.stroke} strokeWidth="2.5" strokeLinecap="round" d="M78 56L89 47M65 53Q70.5 44 76 53" />
        <path fill="none" stroke={p.leaf} strokeWidth="2" strokeLinecap="round" d="M90 51V54M93 49V52" />
      </>);
    case 'elephant':
      return (<>
        {/* heavy body on the pedestal */}
        <path {...main} d="M31 73Q29 56 38 50H62Q71 56 69 73Z" />
        {/* ears */}
        <path {...main} d="M35 33Q14 28 16 50Q23 58 37 48Z" />
        <path {...main} d="M65 33Q86 28 84 50Q77 58 63 48Z" />
        {/* head */}
        <path {...main} d="M34 50Q30 27 50 23Q70 27 66 50Q50 57 34 50Z" />
        {/* trunk curling forward */}
        <path {...main} d="M45 45Q41 61 50 68Q58 72 62 63L58 61Q54 65 50.5 61Q48 55 53 45Z" />
        {/* tusk */}
        <path fill={p.fill} stroke={p.stroke} strokeWidth="2.5" strokeLinejoin="round" d="M57 51Q65 56 68 47Q63 51 58 48Z" />
        <circle cx="41" cy="38" r="2.6" fill={p.detail} />
        <circle cx="59" cy="38" r="2.6" fill={p.detail} />
        {/* the illegal jewel */}
        <path fill={p.accent} stroke={p.stroke} strokeWidth="2" d="M50 26L54 31L50 36L46 31Z" />
      </>);
    case 'cockroach':
      return (<>
        {/* six legs behind the body */}
        <path fill="none" stroke={p.stroke} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
          d="M34 50L22 41L16 44M33 59H19L13 63M35 67L24 75L20 72M66 50L78 41L84 44M67 59H81L87 63M65 67L76 75L80 72" />
        {/* antennae */}
        <path fill="none" stroke={p.detail} strokeWidth="2.2" strokeLinecap="round" d="M46 31Q37 17 25 12M54 31Q63 17 75 12" />
        {/* shell, wing seam and head */}
        <path {...main} d="M50 44Q69 45 69 61Q69 76 50 76Q31 76 31 61Q31 45 50 44Z" />
        <path {...line} d="M50 50V75M38 55Q44 58 50 56M62 55Q56 58 50 56" />
        <path {...main} d="M37 46Q50 34 63 46Q50 51 37 46Z" />
        <circle {...main} cx="50" cy="37" r="7.5" />
        <circle cx="46.5" cy="36" r="1.8" fill={p.detail} />
        <circle cx="53.5" cy="36" r="1.8" fill={p.detail} />
      </>);
    case 'cook':
      return (<>
        {/* pan held to the side */}
        <path fill="none" stroke={p.stroke} strokeWidth="4" strokeLinecap="round" d="M63 61L71 56" />
        <ellipse fill={p.metal} stroke={p.stroke} strokeWidth="2.5" cx="80" cy="51" rx="11" ry="8" />
        <ellipse fill="none" stroke={p.detail} strokeWidth="1.6" cx="80" cy="50" rx="6" ry="3.5" opacity="0.6" />
        {/* bishop-style body with apron */}
        <path {...main} d="M36 72Q38 62 44 58H56Q62 62 64 72Z" />
        <path {...main} d="M40 59Q38 50 44 48H56Q62 50 60 59Z" />
        <path fill="none" stroke={p.accent} strokeWidth="2.5" d="M45 49V58M55 49V58" />
        <circle {...main} cx="50" cy="40" r="9.5" />
        <path fill="none" stroke={p.detail} strokeWidth="2.2" strokeLinecap="round" d="M44 44Q47 42 50 44Q53 42 56 44" />
        {/* puffy chef hat */}
        <path {...main} d="M39 31Q31 26 36 18Q40 12 46 15Q50 8 56 13Q64 11 65 19Q69 26 61 31Z" />
        <path {...main} d="M40 30H60V35H40Z" />
      </>);
  }
}

export function PieceIcon({ icon, color, set, className }: { icon: IconId; color: PieceColor; set: PieceSet; className?: string }) {
  const p = PALETTES[set][color];
  return (
    <svg viewBox="0 0 100 100" className={`piece-svg ${set} ${set}-${color} ${className ?? ''}`} aria-hidden="true">
      <g strokeWidth="3.5" strokeLinejoin="round">
        <path fill={p.fill} stroke={p.stroke} d={BASE} />
        <path fill={p.fill} stroke={p.stroke} d={COLLAR} />
        <Body icon={icon} p={p} />
      </g>
    </svg>
  );
}
